// ============================================================================
// AVATAR COGNITION GRAPH
// Read-only visualization of an avatar's cognitive policy graph (Goal/Lens/
// Question/EvidenceRule/DecisionCriterion nodes synced to Neo4j) plus how
// often each element has led to a positive or negative outcome. A lighter
// sibling of CustomerResearchGraph.tsx — reuses its canvas drawing helpers
// and react-force-graph-2d, but skips search/filters/minimap/merge since this
// graph is small, fixed-shape, and not editable here (policy edits happen in
// AvatarBuilder.tsx / EditProjectAvatarModal.tsx's forms).
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { getAvatarCognitionGraph, CognitionGraphNode, CognitionGraphEdge } from '../services/avatarCatalogService';
import { getNodeConfig, getNodeDisplayName, drawNode, drawNodeHitArea } from './project/customerResearchGraphHelpers';

const LEGEND_TYPES = ['AvatarCognition', 'Goal', 'Lens', 'Question', 'EvidenceRule', 'DecisionCriterion'];

interface AvatarCognitionGraphProps {
  avatarId: string;
}

function effectivenessLabel(node: CognitionGraphNode): string | null {
  const pos = node.timesLedToPositive || 0;
  const neg = node.timesLedToNegative || 0;
  if (pos + neg === 0) return null;
  return `${Math.round((pos / (pos + neg)) * 100)}% effective (${pos} positive / ${neg} negative)`;
}

const NodeDetailPanel: React.FC<{ node: CognitionGraphNode; onClose: () => void }> = ({ node, onClose }) => {
  const config = getNodeConfig(node.type);
  const effectiveness = effectivenessLabel(node);
  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <span
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold text-white"
          style={{ backgroundColor: config.color }}
        >
          {config.label}
        </span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X size={16} />
        </button>
      </div>
      <p className="text-sm text-navy leading-relaxed">{node.text || getNodeDisplayName(node)}</p>
      {Array.isArray(node.topics) && node.topics.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {node.topics.map((t: string) => (
            <span key={t} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">
              {t}
            </span>
          ))}
        </div>
      )}
      {effectiveness ? (
        <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">{effectiveness}</p>
      ) : (
        node.type !== 'AvatarCognition' && node.type !== 'Goal' && (
          <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100">No judged outcomes yet.</p>
        )
      )}
    </div>
  );
};

export const AvatarCognitionGraph: React.FC<AvatarCognitionGraphProps> = ({ avatarId }) => {
  const [nodes, setNodes] = useState<CognitionGraphNode[]>([]);
  const [edges, setEdges] = useState<CognitionGraphEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selected, setSelected] = useState<CognitionGraphNode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 800, height: 560 });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAvatarCognitionGraph(avatarId)
      .then(data => {
        if (cancelled) return;
        setNodes(data.nodes || []);
        setEdges(data.edges || []);
      })
      .catch(() => { if (!cancelled) setError('Failed to load reasoning graph'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [avatarId]);

  useEffect(() => {
    // containerRef's div only exists once loading/error/empty-state branches
    // are past (see the early returns below) — on mount this effect fires
    // while still loading, finds a null ref, and previously never re-ran, so
    // the observer never actually attached once the real graph div appeared.
    // Depending on `loading` here re-runs it right after that div mounts.
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setSize({ width: el.clientWidth, height: Math.max(el.clientHeight, 500) });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, nodes.length]);

  const connectionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    edges.forEach(e => {
      counts.set(e.source, (counts.get(e.source) || 0) + 1);
      counts.set(e.target, (counts.get(e.target) || 0) + 1);
    });
    return counts;
  }, [edges]);

  const forceGraphData = useMemo(() => ({
    nodes: nodes.map(n => ({ ...n, __connections: connectionCounts.get(n.id) || 0 })),
    links: edges.map(e => ({ ...e }))
  }), [nodes, edges, connectionCounts]);

  const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
    drawNode(node, ctx, globalScale, {
      isHovered: node.id === hoveredId,
      isSelected: selected?.id === node.id,
      isDimmed: false
    });
  }, [hoveredId, selected]);

  const nodePointerAreaPaint = useCallback((node: any, color: string, ctx: CanvasRenderingContext2D) => {
    drawNodeHitArea(node, color, ctx);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 p-6">
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="rounded-xl bg-white p-12 shadow-sm border border-slate-200 text-center m-6">
        <p className="text-slate-500 text-sm">No cognitive policy defined for this avatar yet.</p>
        <p className="text-slate-400 text-xs mt-2">Add goals, lenses, questions, evidence rules, or decision criteria to see the reasoning graph.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 px-4 py-2.5 border-b border-slate-200 bg-white flex-wrap">
        {LEGEND_TYPES.map(type => {
          const config = getNodeConfig(type);
          return (
            <div key={type} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: config.color }} />
              <span className="text-xs text-slate-500 font-medium">{config.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex flex-1 min-h-0">
        <div ref={containerRef} className="flex-1 relative">
          <ForceGraph2D
            graphData={forceGraphData as any}
            width={size.width}
            height={size.height}
            backgroundColor="#0f172a"
            nodeCanvasObject={nodeCanvasObject}
            nodeCanvasObjectMode={() => 'replace' as any}
            nodePointerAreaPaint={nodePointerAreaPaint}
            linkColor={() => 'rgba(148, 163, 184, 0.4)'}
            linkWidth={1}
            onNodeHover={(node: any) => setHoveredId(node?.id ?? null)}
            onNodeClick={(node: any) => setSelected(node)}
            onBackgroundClick={() => setSelected(null)}
            cooldownTime={2000}
          />
        </div>
        {selected && (
          <div className="w-80 shrink-0 border-l border-slate-200 bg-white p-4 overflow-y-auto">
            <NodeDetailPanel node={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarCognitionGraph;
